import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../components/ui';
import { errMsg, fmt } from '../../utils/format';
import { useFetch } from '../../hooks/useFetch';
import api from '../../utils/api';

export default function AddPurchaseOrderModal({ onClose, onSaved }) {
  const { data: vendors } = useFetch('/api/vendors');
  const { data: materials } = useFetch('/api/raw-materials');
  const [vendorId, setVendorId] = useState('');
  const [notes, setNotes] = useState('');
  const [leadTimeDays, setLeadTimeDays] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [items, setItems] = useState([{ materialId:'', quantity:'', unitPrice:'', discountPct:'0' }]);
  const [saving, setSaving] = useState(false);

  // Filter materials to those assigned to the selected vendor
  const vendorMaterials = (materials||[]).filter(m =>
    !vendorId || String(m.preferred_vendor_id) === vendorId
  );
  const allMaterials = materials || [];

  const setItem = (idx,k,v) => setItems(i=>i.map((row,j) => {
    if (j !== idx) return row;
    const updated = {...row,[k]:v};
    // Auto-apply discount when quantity changes
    if (k === 'quantity') {
      const auto = getAutoDiscount(v);
      updated.discountPct = String(auto);
    }
    return updated;
  }));
  const addItem = () => setItems(i=>[...i,{ materialId:'', quantity:'', unitPrice:'', discountPct:'0' }]);
  const removeItem = (idx) => setItems(i=>i.filter((_,j)=>j!==idx));

  const selectMaterial = (idx, materialId) => {
    const m = allMaterials.find(m=>String(m.id)===materialId);
    if (m) setItems(i=>i.map((row,j)=>j===idx?{...row,materialId,unitPrice:String(m.unit_cost)}:row));
  };

  // Auto-fetch vendor discount tiers when vendor is selected
  const [discountTiers, setDiscountTiers] = useState([]);
  useEffect(() => {
    if (!vendorId) { setDiscountTiers([]); return; }
    api.get(`/api/vendor-discounts?vendorId=${vendorId}`)
      .then(r => setDiscountTiers(r.data || []))
      .catch(() => setDiscountTiers([]));
  }, [vendorId]);

  const getAutoDiscount = useCallback((qty) => {
    if (!discountTiers.length || !qty) return 0;
    const q = parseFloat(qty);
    const applicable = discountTiers
      .filter(t => q >= parseFloat(t.min_quantity))
      .sort((a,b) => b.min_quantity - a.min_quantity);
    return applicable.length ? parseFloat(applicable[0].discount_pct) : 0;
  }, [discountTiers]);

  const handleVendorChange = (v) => {
    setVendorId(v);
    // Clear material selections when vendor changes
    setItems(i => i.map(row => ({ ...row, materialId:'', unitPrice:'' })));
  };

  const total = items.reduce((s,i)=>s+(parseFloat(i.quantity)||0)*(parseFloat(i.unitPrice)||0)*(1-(parseFloat(i.discountPct)||0)/100),0);

  // Calculate expected arrival
  const expectedArrival = (() => {
    if (!issuedDate || !leadTimeDays) return null;
    const d = new Date(issuedDate);
    d.setDate(d.getDate() + parseInt(leadTimeDays));
    return d.toISOString().slice(0,10);
  })();

  const save = async (e) => {
    e.preventDefault();
    if (!vendorId) { alert('Select a vendor'); return; }
    const validItems = items.filter(i=>i.materialId && i.quantity && i.unitPrice);
    if (!validItems.length) { alert('Add at least one line item'); return; }
    setSaving(true);
    try {
      await api.post('/api/purchase-orders', {
        vendorId: parseInt(vendorId),
        notes,
        leadTimeDays: leadTimeDays ? parseInt(leadTimeDays) : null,
        issuedDate: issuedDate || null,
        items: validItems.map(i=>({ materialId:parseInt(i.materialId), quantity:parseFloat(i.quantity), unitPrice:parseFloat(i.unitPrice), discountPct:parseFloat(i.discountPct)||0 }))
      });
      onSaved('Purchase order created successfully'); onClose();
    } catch(err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Purchase Order" onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="add-po-form" type="submit">{saving?'Saving…':'Create PO'}</button></>}>
      <form id="add-po-form" onSubmit={save}>
        <div className="form-group">
          <label>Vendor *</label>
          <select required value={vendorId} onChange={e=>handleVendorChange(e.target.value)}>
            <option value="">— Select vendor —</option>
            {(vendors||[]).map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>

        {discountTiers.length > 0 && (
          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:4, padding:'4px 8px', background:'var(--bg-secondary)', borderRadius:4 }}>
            <i className="ti ti-discount" style={{ marginRight:4 }} />
            Vendor discounts: {discountTiers.sort((a,b)=>a.min_quantity-b.min_quantity).map(t=>`${t.discount_pct}% over ${parseFloat(t.min_quantity).toLocaleString()}`).join(' · ')}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginTop:8 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Issued date</label>
            <input type="date" value={issuedDate} onChange={e=>setIssuedDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Lead time (days)</label>
            <input type="number" min="0" placeholder="e.g. 14" value={leadTimeDays} onChange={e=>setLeadTimeDays(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Expected arrival</label>
            <input type="date" value={expectedArrival||''} readOnly style={{ background:'var(--bg-secondary)', color: expectedArrival ? 'var(--text-primary)' : 'var(--text-tertiary)' }} />
          </div>
        </div>

        <div style={{ fontWeight:500, fontSize:13, marginBottom:8, marginTop:16 }}>Line items</div>
        {items.map((item,idx) => (
          <div key={idx} style={{ display:'grid', gridTemplateColumns:'3fr 1fr 1fr 0.7fr auto', gap:8, marginBottom:8, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Raw material</label>}
              <select value={item.materialId} onChange={e=>selectMaterial(idx,e.target.value)}>
                <option value="">— Select —</option>
                {vendorMaterials.length
                  ? vendorMaterials.map(m=><option key={m.id} value={m.id}>{m.name} ({m.unit_of_measure})</option>)
                  : allMaterials.map(m=><option key={m.id} value={m.id}>{m.name} ({m.unit_of_measure})</option>)
                }
              </select>
              {vendorId && vendorMaterials.length === 0 && <div style={{ fontSize:11, color:'var(--color-warning)', marginTop:2 }}>No materials assigned to this vendor — showing all</div>}
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Quantity</label>}
              <input type="number" step="1" min="0" placeholder="Qty" value={item.quantity} onChange={e=>setItem(idx,'quantity',e.target.value)} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Unit price ($)</label>}
              <input type="number" step="0.0001" min="0" placeholder="Price" value={item.unitPrice} onChange={e=>setItem(idx,'unitPrice',e.target.value)} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Discount (%)</label>}
              <input type="number" step="0.01" min="0" max="100" placeholder="0" value={item.discountPct} onChange={e=>setItem(idx,'discountPct',e.target.value)} />
            </div>
            <button type="button" className="btn btn-sm btn-danger" style={{ marginTop:idx===0?18:0 }} onClick={()=>removeItem(idx)}>
              <i className="ti ti-x" />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={addItem} style={{ marginTop:4 }}>
          <i className="ti ti-plus" /> Add line
        </button>
        {total > 0 && <div style={{ textAlign:'right', marginTop:12, fontWeight:500 }}>Estimated total: {fmt.currency(total)}</div>}
        <div className="form-group" style={{ marginTop:12 }}><label>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} /></div>
      </form>
    </Modal>
  );
}
